import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { useApi, useApiProblem } from '@/shared/api';
import type { BookingCancellationRefundPolicy } from '@/shared/api';
import { formatDate } from '@/shared/lib/format';
import { CancellationAmounts, PaidAdminOnlyHint, RefundDecision } from './CancelBookingPayment';
import { bookingMutationErrorText } from './bookingMutationErrors';
import { cancellationRequest } from './cancellationRequest';
import { UnfinishedCancellationNotice } from './UnfinishedCancellationNotice';
import { useCancelBooking } from './useBookingMutations';

interface CancelBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  guestName: string;
  checkInDate: string;
  checkOutDate: string;
}

export function CancelBookingDialog({
  open,
  onOpenChange,
  bookingId,
  guestName,
  checkInDate,
  checkOutDate,
}: CancelBookingDialogProps) {
  const api = useApi();
  const admin = isWorkspaceAdmin(useAuth().user);
  const [decision, setDecision] = useState<BookingCancellationRefundPolicy>();
  const operationId = useRef<string | null>(null);
  const preview = useQuery({
    queryKey: ['bookings', 'cancellation-preview', bookingId],
    queryFn: () => api.bookings.cancellationPreview(bookingId),
    enabled: open,
  });
  const previewProblem = useApiProblem(preview.error);
  const cancel = useCancelBooking(bookingId, () => onOpenChange(false));

  useEffect(() => {
    if (!open) {
      setDecision(undefined);
      operationId.current = null;
    }
  }, [open]);

  const data = preview.data;
  const unfinished = data?.unfinishedOperation ?? null;
  const paidDecision = !unfinished && (data?.requiresRefundDecision ?? false);
  useEffect(() => {
    if (open && paidDecision && !decision) setDecision('FullRemaining');
  }, [decision, open, paidDecision]);
  const canSubmit =
    !!data?.canCancel &&
    !preview.isLoading &&
    !cancel.isPending &&
    (!paidDecision || (admin && decision === 'FullRemaining'));

  function submit() {
    if (!canSubmit || !data) return;
    operationId.current ??= crypto.randomUUID();
    cancel.mutate(cancellationRequest(data, decision, operationId.current));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showClose={!cancel.isPending}>
        <DialogHeader>
          <DialogTitle>Buchung stornieren?</DialogTitle>
          <DialogDescription>
            {guestName} · {formatDate(checkInDate)} → {formatDate(checkOutDate)}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {preview.isLoading && (
            <p className="text-ink-soft text-sm" role="status">
              Zahlungsstatus wird bei Stripe geprüft…
            </p>
          )}
          {preview.isError && (
            <p className="border-sealwax/30 bg-sealwax/5 text-sealwax rounded-md border p-3 text-sm">
              {previewProblem
                ? bookingMutationErrorText(
                    previewProblem,
                    'Der Storno lässt sich gerade nicht vorbereiten. Die Buchung wurde nicht verändert.',
                  )
                : 'Der Stand des Stornos wird geprüft …'}
            </p>
          )}
          {unfinished && <UnfinishedCancellationNotice operation={unfinished} />}
          {data && !unfinished && !paidDecision && (
            <p className="text-ink-soft text-sm">
              Die offene Zahlung wird geschlossen und der gebuchte Zeitraum wieder freigegeben.
            </p>
          )}
          {data && (paidDecision || (unfinished && data.paidAmountMinor > 0)) && (
            <CancellationAmounts preview={data} />
          )}
          {data && (paidDecision || unfinished) && !admin && data.paidAmountMinor > 0 && (
            <PaidAdminOnlyHint />
          )}
          {data && paidDecision && admin && (
            <RefundDecision decision={decision} onDecision={setDecision} />
          )}
          <p className="text-ink-faint text-xs">
            Bei einer vollständigen Stornierung wird eine vorhandene Rechnung automatisch durch eine
            Stornorechnung in der digitalen Akte korrigiert. Der Gastzugang wird gesperrt und der
            Gast erhält eine Bestätigung per E-Mail.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={cancel.isPending}>
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            loading={cancel.isPending}
            disabled={!canSubmit}
            onClick={submit}
          >
            {unfinished ? 'Storno abschließen' : 'Buchung stornieren'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
